// The board's half of the path. Every call here goes to a Function on this
// origin — never to the trip service, which this bundle does not know the
// address of and could not authenticate to if it did.
//
// 🔴 There is no token in this file, and there is nowhere in the bundle for one
// to live. ADR-0003 D5: personal data arrives through a Function at runtime and
// is never built into the site, "so there is nothing crew-shaped in the bundle
// to protect." The same argument covers the credential — the service token is
// read on the Workers runtime inside site/functions/_lib/trips.js and attached
// to a server-to-server request. src/test/functions/leak-sweep.test.js proves
// it does not come back out.
//
// `credentials: 'same-origin'` on every call: the session cookie IS the
// caller's identity, and a fetch that dropped it would be answered as signed
// out.

const HEADERS = { Accept: 'application/json' }

/** A failure a member can read.
 *
 * The Functions answer failures as plain text in our own words (a closed table,
 * never an upstream body), so the text of a non-OK response IS the sentence to
 * show. `status` is carried for the caller to distinguish "signed out" from
 * "everything else" and for nothing else.
 */
export class BoardError extends Error {
  constructor(message, status) {
    super(message)
    this.name = 'BoardError'
    this.status = status
  }
}

const UNREACHABLE =
  'The trips service is unreachable, so the board cannot load. Nothing you did was lost.'

async function send(path, { method = 'GET', body, fetchImpl } = {}) {
  const doFetch = fetchImpl ?? globalThis.fetch
  let response
  try {
    response = await doFetch(path, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? HEADERS : { ...HEADERS, 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    // §8: the site says the trips are unreachable, plainly. Offline, DNS,
    // the Function itself down — all the same fact to the person reading it.
    throw new BoardError(UNREACHABLE, 0)
  }

  if (!response.ok) {
    let text = ''
    try {
      text = (await response.text()).trim()
    } catch {
      text = ''
    }
    throw new BoardError(text === '' ? UNREACHABLE : text, response.status)
  }

  try {
    return await response.json()
  } catch {
    throw new BoardError(UNREACHABLE, response.status)
  }
}

export const listTrips = (fetchImpl) => send('/api/trips', { fetchImpl })

export const createTrip = (body, fetchImpl) =>
  send('/api/trips', { method: 'POST', body, fetchImpl })

/** The whole board in one answer — trip, entries, destinations, estimate, and
 * (from T5) the `cursor` the live channel starts from.
 *
 * ⚠️ STILL POLL-ON-LOAD, and that is not a leftover. This is the request the
 * page makes when it opens, again after the member's own write, and again
 * whenever the changes feed says somebody else changed something — one request
 * shape for all three, because the honesty rules are claims about a SET and
 * the set and the claim have to be read together (see the Function).
 *
 * 🔴 THERE IS STILL NO `setInterval` HERE, and there must not be. The polling
 * that T5 added is a poll of the CHANGES FEED with a cursor, in
 * `src/trips/live.js`; a timer re-reading the whole board would be the same
 * liveness on a much worse transport, and would have no way to tell "nothing
 * happened" from "everything happened". */
export const loadBoard = (tripId, fetchImpl) =>
  send(`/api/trips/${encodeURIComponent(tripId)}`, { fetchImpl })

// ── who is on the trip (P6-81) ─────────────────────────────────────────────
//
// 🔴 THE SAME `send`, THE SAME ORIGIN, NO NEW TRANSPORT — the rule every slice
// since T6 has followed. Both calls below append a `change` row through the
// service's `mutation()` seal, so they ride down the tail the board already
// tails and `src/trips/live.js` is untouched.

/** Add people to this trip, take people off it, or both — as a DELTA.
 *
 * 🔴 `{add, remove}`, NEVER the whole list, and the reason is not stylistic.
 * The picker's snapshot can be stale by the time Save is pressed; sending the
 * list it loaded would silently uninvite anybody a second session added in
 * between, and the person it costs is the one who was invited and then quietly
 * was not. `trips/routes/trips.py` applies the delta in one statement inside
 * Postgres so two of these can land at once.
 */
export const setInvitees = (tripId, { add = [], remove = [] }, fetchImpl) =>
  send(`/api/trips/${encodeURIComponent(tripId)}/invitees`, {
    method: 'POST',
    body: { add, remove },
    fetchImpl,
  })

/** "Anyone in the server" ⇄ "only the people on it". Organiser only — the
 * service refuses anybody else and this does not re-derive that rule. */
export const setVisibility = (tripId, visibility, fetchImpl) =>
  send(`/api/trips/${encodeURIComponent(tripId)}`, {
    method: 'PATCH',
    body: { visibility },
    fetchImpl,
  })

export const addEntry = (tripId, body, fetchImpl) =>
  send(`/api/trips/${encodeURIComponent(tripId)}/entries`, { method: 'POST', body, fetchImpl })

export const editEntry = (entryId, body, fetchImpl) =>
  send(`/api/entries/${encodeURIComponent(entryId)}`, { method: 'PATCH', body, fetchImpl })

/** "Try for a preview" — P6-82.
 *
 * 🔴 THE ANSWER TO THIS CALL IS NOT WHERE THE PHOTO COMES FROM, and a caller
 * that renders it would be building the second mechanism §5.6 forbids. The
 * service's unfurl writes through its `mutation()` seal, so a stored photo
 * appends a `change` row and reaches the open board down the tail `live.js`
 * already holds. What this resolves to is only "the ask did not fail"; what it
 * REJECTS with is the one thing the feed cannot carry — a refusal, which
 * writes nothing and therefore appends nothing.
 *
 * ⚠️ Every link added from the site is already asked about, server-side, by the
 * Function that creates the entry. This is the door for the entries that were
 * added before that existed, and for a host that was briefly having a bad time.
 */
export const unfurlEntry = (entryId, fetchImpl) =>
  send(`/api/entries/${encodeURIComponent(entryId)}/unfurl`, { method: 'POST', fetchImpl })

/** ⭐/👍. Passing `null` clears — "clicking the active level clears it". */
export const setPreference = (entryId, level, fetchImpl) =>
  level === null
    ? send(`/api/entries/${encodeURIComponent(entryId)}/preference`, {
        method: 'DELETE',
        fetchImpl,
      })
    : send(`/api/entries/${encodeURIComponent(entryId)}/preference`, {
        method: 'PUT',
        body: { level },
        fetchImpl,
      })

// ── the date poll (P5-3 T6) ────────────────────────────────────────────────
//
// 🔴 THE SAME `send` AND THE SAME FUNCTIONS ROOT AS THE BOARD. The dates screen
// is another view of one trip, not another product: one origin, one session
// cookie, one credential (which is not in this bundle), and — the part that
// matters for §5.6 — NO NEW TRANSPORT. Its liveness is the changes feed the
// board already tails, so `src/trips/live.js` is untouched by this slice.

/** The poll, its answers, its ranking, and the cursor the live channel starts
 * from. Shaped like `loadBoard`: `{ you, cursor, poll }`. */
export const loadDates = (tripId, fetchImpl) =>
  send(`/api/trips/${encodeURIComponent(tripId)}/range-poll`, { fetchImpl })

/** Ask the question. Organiser only — the service refuses anybody else, and
 * this does not re-derive that rule. */
export const proposeRanges = (tripId, body, fetchImpl) =>
  send(`/api/trips/${encodeURIComponent(tripId)}/range-poll`, {
    method: 'PUT',
    body,
    fetchImpl,
  })

/** Every range that works for me. **An empty array is a real answer** — "none
 * of these work for me" — and is sent, not skipped. */
export const sendSelection = (tripId, rangeIds, fetchImpl) =>
  send(`/api/trips/${encodeURIComponent(tripId)}/range-poll/selection`, {
    method: 'PUT',
    body: { range_ids: rangeIds },
    fetchImpl,
  })

/** Confirm one range, named. There is no "confirm the top one" call, here or
 * in the service: TR8 is *"organiser confirms, never auto-picks"*. */
export const confirmRange = (tripId, rangeId, fetchImpl) =>
  send(`/api/trips/${encodeURIComponent(tripId)}/range-poll/confirm`, {
    method: 'POST',
    body: { range_id: rangeId },
    fetchImpl,
  })

/** Undo a confirmation. Part of the designed flow — the green band carries it. */
export const undoConfirmation = (tripId, fetchImpl) =>
  send(`/api/trips/${encodeURIComponent(tripId)}/range-poll/confirm`, {
    method: 'DELETE',
    fetchImpl,
  })

// ── the comparison (P5-3 T9) ───────────────────────────────────────────────
//
// 🔴 THE SAME `send`, THE SAME ORIGIN, AND — the part §5.6 cares about — NO
// NEW TRANSPORT. Every mutation below appends a `change` row through the
// service's `mutation()` seal, so it rides down the tail the board already
// tails: *"The live channel **is** a tail of this feed ... No second mechanism
// for any of the three, ever."* `src/trips/live.js` is untouched by this slice,
// exactly as it was untouched by T6.

/** Trip, entries, ALL destinations including archived, and the estimate with
 * their roll-ups — in one answer, from one moment. Shaped like `loadBoard`.
 *
 * A separate endpoint from the board's rather than a flag on it: the board must
 * never learn to render an archived place. See the Function. */
export const loadCompare = (tripId, fetchImpl) =>
  send(`/api/trips/${encodeURIComponent(tripId)}/compare`, { fetchImpl })

/** "Compare another place."
 *
 * `existing_name` names what is already on the board, and is required by the
 * Function when the trip has no places yet — that call also attaches the
 * board's loose entries to it, so the answer carries `attached` / `attachable`
 * and the caller says the difference out loud. */
export const addPlace = (tripId, body, fetchImpl) =>
  send(`/api/trips/${encodeURIComponent(tripId)}/destinations`, {
    method: 'POST',
    body,
    fetchImpl,
  })

/** "Choose Portugal" — §5.3, which archives the others in the same call.
 *
 * There is no "choose the cheapest" call, here or in the service. The screen
 * ranks nothing and picks nothing; a person does. */
export const chooseDestination = (tripId, destinationId, fetchImpl) =>
  send(`/api/trips/${encodeURIComponent(tripId)}/choose`, {
    method: 'POST',
    body: { destination_id: destinationId },
    fetchImpl,
  })

/** "Reopen the comparison." Un-archives everything and clears the winner —
 * and destroys nothing, because there was never anything destroyed to restore. */
export const reopenComparison = (tripId, fetchImpl) =>
  send(`/api/trips/${encodeURIComponent(tripId)}/choose`, { method: 'DELETE', fetchImpl })

/** "Start next year's trip from this." Answers `{trip, copied, total}` — the
 * count is not decoration, see the Function. */
export const startTripFrom = (tripId, body, fetchImpl) =>
  send(`/api/trips/${encodeURIComponent(tripId)}/copy`, { method: 'POST', body, fetchImpl })

/** Put a loose entry with a place. `null` takes it back off one. */
export const moveEntry = (entryId, destinationId, fetchImpl) =>
  editEntry(entryId, { destination_id: destinationId }, fetchImpl)

/** The decoration — distances, weather, and the converted figure. P5-3 T10.
 *
 * 🔴 A SECOND REQUEST, NOT A FIELD ON THE BOARD, and §6 says why: *"Decoration
 * may fail; **the grid may not.**"* Three third-party providers sit behind
 * this; none of them may be able to delay or fail the answer that carries
 * everybody's cards.
 *
 * ⚠️ SO EVERY CALLER MUST CATCH IT SEPARATELY. A caller that awaited this in
 * the same `try` as `loadBoard` would have re-coupled the two in the one place
 * the whole design was arranged to keep apart — and the failure would look
 * exactly like the trip service being down. `Promise.allSettled`, or a
 * `.catch(() => null)`, and never `Promise.all`.
 */
export const loadEnrichment = (tripId, { archived = false, fetchImpl } = {}) =>
  send(
    `/api/trips/${encodeURIComponent(tripId)}/enrichment` +
      (archived ? '?include_archived=true' : ''),
    { fetchImpl },
  )

export { UNREACHABLE }
